import { useCallback, useEffect, useState } from "react";
import { checkStaffGeofence, type GeofenceReason } from "../lib/geofence";
import { usePermissionStatuses } from "./usePermissionStatuses";

export type LocationGateStatus = "checking" | "not_required" | "needs_gesture" | Exclude<GeofenceReason, "no_store_location">;

/**
 * Gates the LOGIN screens themselves (staff picker + PIN entry) — not just
 * the act of clocking in, which useStaffSession's own geofenceBlockError
 * already covers. Runs the moment the paired store's location has loaded
 * (i.e. on every reload, since a reload remounts StaffGate from scratch),
 * so a device that's plainly not at the store — or has no usable location
 * reading at all — never even gets to show who's on the roster.
 *
 * Deliberately has nothing to say about an already-signed-in/resumed
 * session (see StaffGate, which only consults this before login is
 * shown) — ending an active shift over this would repeat the exact
 * mistake the auto clock-out poller's own leniency was built to avoid.
 */
export function useStoreLocationGate(
  store: { latitude: string | null; longitude: string | null; geofence_radius_m?: number | null } | null,
  storeReady: boolean,
  /** Platform-owner debugging only — see core.devices.bypass_geofence's own
   * doc. Skips this whole gate unconditionally, regardless of the store's
   * own geofence configuration. */
  bypass = false,
) {
  const { statuses, loading: permLoading, recheck: recheckPermission } = usePermissionStatuses(["geolocation"]);
  const [status, setStatus] = useState<LocationGateStatus>("checking");
  const [distanceM, setDistanceM] = useState<number | undefined>(undefined);

  // Actually calls checkStaffGeofence (and so, getCurrentPosition) — only
  // ever invoked from a place that's either already known-safe (permission
  // already "granted", so no prompt will show) or from a real tap. Chrome
  // (and Safari, for camera/location alike for a while now) silently
  // ignore a getCurrentPosition call that isn't triggered by a user
  // gesture — the call just hangs until its own timeout instead of ever
  // showing the permission dialog. See the effect below for the "prompt"/
  // "unsupported" case this protects against.
  const runCheck = useCallback(() => {
    if (bypass || !store || store.latitude == null || store.longitude == null) {
      setStatus("not_required");
      return;
    }
    setStatus("checking");
    void checkStaffGeofence(store).then((result) => {
      setDistanceM(result.distanceM);
      setStatus(result.reason === "no_store_location" ? "not_required" : result.reason);
    });
  }, [bypass, store?.latitude, store?.longitude, store?.geofence_radius_m]);

  useEffect(() => {
    if (bypass) {
      setStatus("not_required");
      return;
    }
    if (!storeReady || permLoading) {
      setStatus("checking");
      return;
    }
    if (!store || store.latitude == null || store.longitude == null) {
      setStatus("not_required");
      return;
    }
    // Known-denied from the Permissions API needs no sampling wait — skip
    // straight to the blocked state instead of stalling on a
    // getCurrentPosition call that's guaranteed to fail anyway.
    if (statuses.geolocation === "denied") {
      setStatus("denied");
      return;
    }
    // Already granted (a returning device that's done this before) — no
    // prompt will show, so it's safe to check automatically with no tap
    // needed.
    if (statuses.geolocation === "granted") {
      runCheck();
      return;
    }
    // "prompt" (never asked on this device/browser yet) or "unsupported"
    // (can't tell ahead of time) — this is exactly the case that gets
    // silently ignored without a user gesture. Wait for an explicit tap
    // instead (see StaffGate's "needs_gesture" screen, whose button calls
    // confirmLocation below directly from its own onClick).
    setStatus("needs_gesture");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bypass, store?.latitude, store?.longitude, store?.geofence_radius_m, storeReady, permLoading, statuses.geolocation]);

  return {
    status,
    distanceM,
    /** "Try again" on the blocked screen — already gesture-driven since
     * it's only ever wired to a button's onClick. */
    recheck: () => {
      recheckPermission();
      runCheck();
    },
    /** The needs_gesture screen's own button calls this directly — this
     * click IS the user gesture the very first permission prompt needs. */
    confirmLocation: runCheck,
  };
}
